import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import AuthShell from '../../components/auth/AuthShell';
import { slides, PRODUCT_LABEL } from '../../components/auth/slides';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

export default function SignupSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [dots, setDots] = useState('');
  const [status, setStatus] = useState('pending'); // 'pending' | 'ready' | 'timeout' | 'error'
  const [email, setEmail] = useState('');
  const pollStartedAt = useRef(Date.now());

  // Animate dots while pending
  useEffect(() => {
    if (status !== 'pending') return;
    const t = setInterval(() => setDots(d => (d.length >= 3 ? '' : d + '.')), 500);
    return () => clearInterval(t);
  }, [status]);

  // Poll for provisioning completion
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    const getSignupStatus = httpsCallable(functions, 'getSignupStatus');

    const poll = async () => {
      if (cancelled) return;
      try {
        const result = await getSignupStatus({ sessionId });
        const data = result.data || {};
        if (data.status === 'provisioned') {
          if (!cancelled) {
            setEmail(data.email || '');
            setStatus('ready');
          }
          return;
        }
        if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
          if (!cancelled) setStatus('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        console.error('Poll error', err);
        // Keep polling on transient errors, until timeout
        if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
          if (!cancelled) setStatus('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <AuthShell productLabel={PRODUCT_LABEL} slides={slides}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(46,160,67,0.12)',
          color: '#2ea043',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          margin: '0 auto 18px',
        }}>✓</div>

        <h1 style={{ fontSize: 22, fontWeight: 500, margin: '0 0 10px' }}>Payment received</h1>

        {status === 'pending' && (
          <>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
              We're setting up your account{dots}
            </p>
            <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', margin: 0 }}>This usually takes 5–10 seconds.</p>
          </>
        )}

        {status === 'ready' && (
          <>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
              Your account is ready. We've sent a welcome email to <strong>{email}</strong> with a link to set your password.
            </p>
            <div style={{ padding: 14, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', textAlign: 'left', marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                Check your inbox. If you don't see the email within a couple of minutes, check your spam folder.
              </div>
            </div>
            <a href="/login" style={{
              display: 'inline-block',
              padding: '10px 20px',
              background: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
              border: '0.5px solid var(--color-border-light)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
              textDecoration: 'none',
            }}>Go to login</a>
          </>
        )}

        {status === 'timeout' && (
          <>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
              Your payment was received, but account setup is taking longer than expected. Don't worry — it'll complete in the background.
            </p>
            <div style={{ padding: 14, background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', textAlign: 'left', marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                You'll receive a welcome email with a link to set your password within a few minutes. If you don't get it after 10 minutes, please contact support.
              </div>
            </div>
          </>
        )}

        {status === 'error' && (
          <p style={{ fontSize: 14, color: 'var(--color-danger)', lineHeight: 1.6, margin: '0 0 20px' }}>
            Something went wrong loading your signup status. Your payment was received and your account is being set up — please check your email shortly.
          </p>
        )}

        <div style={{ marginTop: 24, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
          {sessionId ? `Reference: ${sessionId.slice(-12)}` : ''}
        </div>
      </div>
    </AuthShell>
  );
}
